import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Drawer, message, Spin } from "antd";
import { RocketOutlined, SyncOutlined, RightOutlined } from "@ant-design/icons";

import MultiNetworkConnectedCard from "../components/MultiNetworkConnectedCard";
import MultiNetworkDepositDrawer from "../components/MultiNetworkDepositDrawer";
import WorkingBalanceWithdrawDrawer from "../components/WorkingBalanceWithdrawDrawer";
import NetworkSelector from "../components/NetworkSelector";
import {
  CURRENCIES,
  formatAmount,
  SupportedNetwork,
  SupportedCurrency,
  WORKING_BALANCE_ORDER,
  currencyIcon,
  currencyLabel,
} from "../constants/networks";
import {
  fetchOvernightInternalBalances,
  getVoucherStatus,
  VoucherStatus,
} from "../helpers/api";
import { useMultiWallet } from "../hooks/useMultiWallet";
import { usePublicRates, rateOr, demandRateOr } from "../hooks/usePublicRates";
import { walletFamilyOf } from "../helpers/walletLink";
import { RootState } from "../services/store";
import { isLegacyWebtransferBrand } from "../utils/miniAppBrand";
import "./kredafi.css";

const SHORT_NAME: Record<SupportedNetwork, string> = {
  ton: "TON",
  polygon: "Polygon",
  bsc: "BNB Chain",
  ethereum: "Ethereum",
  solana: "Solana",
  tron: "TRON",
};

interface KredafiHomeProps {
  voucher?: VoucherStatus | null;
  externalVoucherTrigger?: boolean;
  onVoucherDrawerClose?: () => void;
}

const KredafiHome: React.FC<KredafiHomeProps> = ({
  voucher: voucherProp,
  externalVoucherTrigger,
  onVoucherDrawerClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, isLoading } = useSelector((state: RootState) => state.user);
  const multiWallet = useMultiWallet();
  const publicRates = usePublicRates();

  const overnightRate = userData?.settings?.max_rate_overnight;
  const overnightRateNum = (() => {
    const fromAccount = Number(overnightRate);
    if (Number.isFinite(fromAccount) && fromAccount > 0) return fromAccount;
    return rateOr(publicRates, "max_rate_overnight", 0.3);
  })();
  const demandFromUser = Number(userData?.settings?.demand_rate_overnight);
  const demandRateNum =
    Number.isFinite(demandFromUser) && demandFromUser > overnightRateNum
      ? demandFromUser
      : demandRateOr(publicRates, overnightRateNum);

  const [voucher, setVoucher] = useState<VoucherStatus | null>(voucherProp || null);
  const [internalBalances, setInternalBalances] = useState<{
    usdt: number;
    ton: number;
  } | null>(null);
  const [assets, setAssets] = useState<Record<string, number>>({});
  const [prices, setPrices] = useState<Record<string, number | null>>({});
  const [viewMode, setViewMode] = useState<"portfolio" | "asset">(() => {
    try {
      return (localStorage.getItem("kf_portfolio_view_mode") as "portfolio" | "asset") || "portfolio";
    } catch {
      return "portfolio";
    }
  });

  const handleToggleViewMode = (mode: "portfolio" | "asset") => {
    setViewMode(mode);
    try {
      localStorage.setItem("kf_portfolio_view_mode", mode);
    } catch {}
  };

  const [isIssueLoanDrawerOpen, setIsIssueLoanDrawerOpen] = useState(false);
  const [isMultiDepositOpen, setIsMultiDepositOpen] = useState(false);
  const [isWorkingWithdrawOpen, setIsWorkingWithdrawOpen] = useState(false);

  useEffect(() => {
    if (voucherProp !== undefined && voucherProp !== null) {
      setVoucher(voucherProp);
    }
  }, [voucherProp]);

  const loadInternal = async () => {
    try {
      const res = await fetchOvernightInternalBalances(true);
      if (res.success) {
        setInternalBalances({
          usdt: res.balance_overnight_usdt,
          ton: res.balance_overnight_ton,
        });
        if (res.assets) setAssets(res.assets);
        if (res.prices) setPrices(res.prices);
      }
    } catch {
      /* сеть могла моргнуть — баланс подтянется следующим заходом */
    }
  };

  useEffect(() => {
    loadInternal();
    const onPfDone = () => loadInternal();
    window.addEventListener("pf-withdraw-success", onPfDone);
    return () => window.removeEventListener("pf-withdraw-success", onPfDone);
  }, []);

  useEffect(() => {
    if (!userData?.id || voucherProp) return;
    getVoucherStatus().then((v) => setVoucher(v));
  }, [userData?.id, voucherProp]);

  useEffect(() => {
    if (externalVoucherTrigger) {
      setIsMultiDepositOpen(true);
    }
  }, [externalVoucherTrigger]);

  const [selectedAsset, setSelectedAsset] = useState<SupportedCurrency>("USDT");
  const allAssets: Record<string, number> = {
    USDT: Number(internalBalances?.usdt ?? userData?.balance_overnight_usdt ?? 0),
    TON: Number(internalBalances?.ton ?? userData?.balance_overnight_ton ?? 0),
    ...assets,
  };

  const getAssetPriceUsd = (code: string): number => {
    if (prices[code] !== undefined && prices[code] !== null && Number(prices[code]) > 0) {
      return Number(prices[code]);
    }
    if (code === "USDT" || code === "USDC") return 1.0;
    return 0;
  };

  const portfolioTotalUsd = useMemo(() => {
    let total = 0;
    for (const [code, amount] of Object.entries(allAssets)) {
      const num = Number(amount) || 0;
      if (num <= 0) continue;
      const price = getAssetPriceUsd(code);
      total += num * price;
    }
    return total;
  }, [allAssets, prices]);

  const primary = multiWallet.currentMeta.tokens;
  const currentAssetAmount = allAssets[selectedAsset] ?? 0;
  const displayAddr =
    multiWallet.displayAddresses[multiWallet.activeNetwork] ||
    multiWallet.currentAddress;
  const linked = multiWallet.linkedAddresses[multiWallet.activeNetwork];
  const connected = multiWallet.isConnected;
  const showVoucher =
    voucher && voucher.status !== "expired" && voucher.status !== "disabled";

  const connectError = (e: any) => {
    const code = e?.message || "";
    if (/reject|cancel|user/i.test(code)) return;
    message.error(
      code === "connector_unavailable"
        ? (t(
            "Wallet connector is unavailable. Reload the page or try another browser."
          ) as string)
        : code === "tron_install_extension"
        ? (t("Install the TronLink extension to connect a TRON wallet.") as string)
        : code
    );
  };

  return (
    <>
      {showVoucher && voucher.available && (
        <div style={{ padding: "16px 18px 0" }}>
          <div className="kf-voucher">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  font: "500 10.5px Geist Mono, monospace",
                  letterSpacing: "0.07em",
                  color: "#0aa8a5",
                }}
              >
                {t("PERSONAL 2X VOUCHER")}
              </span>
              {voucher.valid_until && (
                <span
                  style={{
                    marginLeft: "auto",
                    font: "400 11.5px Geist, sans-serif",
                    color: "#5b6779",
                  }}
                >
                  {voucher.valid_until}
                </span>
              )}
            </div>
            <div
              style={{
                marginTop: 10,
                font: "600 20px/1.2 Geist, sans-serif",
                letterSpacing: "-0.03em",
              }}
            >
              {t("Capital Doubling Voucher")}
            </div>
            <p
              style={{
                margin: "8px 0 0",
                font: "400 13.5px/1.5 Geist, sans-serif",
                color: "#5b6779",
              }}
            >
              {isLegacyWebtransferBrand
                ? t("Double the amount of your next top-up in GRAM or USDT!")
                : t("Double the amount of your next deposit — any currency, any network.")}
            </p>
            <button
              type="button"
              className="kf-btn kf-btn-teal"
              style={{
                width: "100%",
                marginTop: 14,
                padding: "12px 0",
                borderRadius: 9,
                font: "600 14.5px Geist, sans-serif",
              }}
              onClick={() => {
                setIsMultiDepositOpen(true);
              }}
            >
              {t("Activate Voucher")}
            </button>
          </div>
        </div>
      )}

      <div style={{ padding: "22px 18px 0" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div className="kf-cap">
            {viewMode === "portfolio" ? t("Portfolio Total").toString().toUpperCase() : t("Work Balance").toString().toUpperCase()}
          </div>
          <div
            style={{
              display: "inline-flex",
              background: "#f1f5f9",
              padding: "2px",
              borderRadius: "8px",
              gap: "2px",
            }}
          >
            <button
              type="button"
              onClick={() => handleToggleViewMode("portfolio")}
              style={{
                border: "none",
                background: viewMode === "portfolio" ? "#ffffff" : "transparent",
                color: viewMode === "portfolio" ? "#0f172a" : "#64748b",
                fontWeight: viewMode === "portfolio" ? 600 : 500,
                fontSize: "11px",
                padding: "3px 8px",
                borderRadius: "6px",
                cursor: "pointer",
                boxShadow: viewMode === "portfolio" ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                transition: "all 0.15s ease",
              }}
            >
              💵 {t("Total USD")}
            </button>
            <button
              type="button"
              onClick={() => handleToggleViewMode("asset")}
              style={{
                border: "none",
                background: viewMode === "asset" ? "#ffffff" : "transparent",
                color: viewMode === "asset" ? "#0f172a" : "#64748b",
                fontWeight: viewMode === "asset" ? 600 : 500,
                fontSize: "11px",
                padding: "3px 8px",
                borderRadius: "6px",
                cursor: "pointer",
                boxShadow: viewMode === "asset" ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                transition: "all 0.15s ease",
              }}
            >
              🪙 {t("By Asset")}
            </button>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
          <span
            style={{
              font: "500 38px Geist Mono, monospace",
              letterSpacing: "-0.04em",
            }}
          >
            {isLoading
              ? "…"
              : viewMode === "portfolio"
              ? `$ ${portfolioTotalUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : formatAmount(selectedAsset, currentAssetAmount)}
          </span>
          <span style={{ font: "500 15px Geist, sans-serif", color: "#64748b" }}>
            {viewMode === "portfolio" ? "USD" : currencyLabel(selectedAsset)}
          </span>
        </div>
        <div
          style={{
            marginTop: 4,
            font: "400 12.5px/1.45 Geist, sans-serif",
            color: "#93a0b3",
          }}
        >
          {viewMode === "portfolio"
            ? t("Aggregate balance across all networks in USD")
            : t("Platform balance, shared across all networks. This is not your wallet balance.")}
        </div>

        {/* Интерактивная карусель всех поддерживаемых активов платформы + сводный портфель */}
        <div className="kf-asset-scroll" role="tablist" aria-label={t("Work Balance") as string}>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "portfolio"}
            className={`kf-asset-card${viewMode === "portfolio" ? " active" : ""}`}
            onClick={() => handleToggleViewMode("portfolio")}
          >
            <div className="kf-asset-card-top">
              <span style={{ fontSize: 14, lineHeight: 1 }}>🌐</span>
              <span className="kf-asset-card-name">{t("Total USD")}</span>
            </div>
            <div className="kf-asset-card-val">
              {isLoading ? "…" : `$${portfolioTotalUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </div>
          </button>
          {WORKING_BALANCE_ORDER.map((code) => {
            const isSelected = viewMode === "asset" && selectedAsset === code;
            const bal = allAssets[code] ?? 0;
            const price = getAssetPriceUsd(code);
            const usdVal = bal * price;
            return (
              <button
                key={code}
                type="button"
                role="tab"
                aria-selected={isSelected}
                className={`kf-asset-card${isSelected ? " active" : ""}`}
                onClick={() => {
                  setSelectedAsset(code);
                  handleToggleViewMode("asset");
                }}
              >
                <div className="kf-asset-card-top">
                  <img src={currencyIcon(code)} alt={code} />
                  <span className="kf-asset-card-name">{currencyLabel(code)}</span>
                </div>
                <div className="kf-asset-card-val">
                  {isLoading ? "…" : formatAmount(code, bal)}
                </div>
                {bal > 0 && price > 0 && code !== "USDT" && code !== "USDC" && (
                  <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2, fontWeight: 500 }}>
                    ≈ ${usdVal.toFixed(2)}
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button
            type="button"
            className="kf-btn kf-btn-navy"
            style={{
              flex: 1,
              padding: "13px 0",
              borderRadius: 10,
              font: "500 14.5px Geist, sans-serif",
            }}
            onClick={() => setIsMultiDepositOpen(true)}
          >
            {t("Top up")}
          </button>
          <button
            type="button"
            className="kf-btn kf-btn-ghost"
            style={{
              flex: 1,
              padding: "13px 0",
              borderRadius: 10,
              font: "500 14.5px Geist, sans-serif",
            }}
            onClick={() => setIsIssueLoanDrawerOpen(true)}
          >
            {t("Place at current rate")}
          </button>
          <button
            type="button"
            className="kf-btn kf-btn-ghost"
            style={{
              padding: "13px 14px",
              borderRadius: 10,
              font: "500 14.5px Geist, sans-serif",
              color: "#5b6779",
            }}
            onClick={() => setIsWorkingWithdrawOpen(true)}
          >
            {t("Withdraw")}
          </button>
        </div>
        <div className="kf-shelves" role="list">
          <button
            type="button"
            className="kf-shelf"
            role="listitem"
            onClick={() => setIsMultiDepositOpen(true)}
          >
            <span className="kf-shelf-name">{t("Available shelf")}</span>
            <span className="kf-shelf-val idle">
              {isLoading
                ? "…"
                : viewMode === "portfolio"
                ? `$ ${portfolioTotalUsd.toFixed(2)} USD`
                : formatAmount(selectedAsset, currentAssetAmount)}
            </span>
            <span className="kf-shelf-meta">{t("Idle funds ready to place")}</span>
          </button>
          <button
            type="button"
            className="kf-shelf"
            role="listitem"
            onClick={() => {
              navigate("/loan-overnight", { state: { demand: false } });
            }}
          >
            <span className="kf-shelf-name">{t("Overnight shelf")}</span>
            <span className="kf-shelf-val">~{overnightRateNum.toFixed(2)}%</span>
            <span className="kf-shelf-meta">{t("Overnight returns next morning")}</span>
          </button>
          <button
            type="button"
            className="kf-shelf"
            role="listitem"
            onClick={() => {
              navigate("/loan-overnight", { state: { demand: true } });
            }}
          >
            <span className="kf-shelf-name">{t("On-demand shelf")}</span>
            <span className="kf-shelf-val">~{demandRateNum.toFixed(2)}%</span>
            <span className="kf-shelf-meta">{t("On-demand stop returns within 24h")}</span>
          </button>
        </div>
      </div>

      <div style={{ padding: "22px 18px 24px" }}>
        <NetworkSelector
          activeNetwork={multiWallet.activeNetwork}
          onSelectNetwork={(net) => {
            multiWallet.setActiveNetwork(net);
            if (net === "tron" && !multiWallet.connectedNetworks.tron) {
              multiWallet.openWalletPicker("tron");
            }
          }}
          connectedNetworks={multiWallet.connectedNetworks}
        />

        <div style={{ display: "flex", alignItems: "center", marginTop: 14 }}>
          <span className="kf-cap">
            {t("Connected Wallet").toString().toUpperCase()} ({SHORT_NAME[multiWallet.activeNetwork]})
          </span>
          {!connected && (
            <button
              type="button"
              className="kf-login"
              style={{ marginLeft: "auto", font: "450 12.5px Geist, sans-serif" }}
              onClick={() =>
                multiWallet.connect().catch(connectError)
              }
            >
              {t("Connect Wallet")}
            </button>
          )}
        </div>
        <div className="kf-card" style={{ marginTop: 10, padding: 14 }}>
          <MultiNetworkConnectedCard
            activeNetwork={multiWallet.activeNetwork}
            isConnected={multiWallet.isConnected}
            currentAddress={multiWallet.currentAddress}
            truncatedAddress={multiWallet.truncatedAddress}
            linkedAddress={linked}
            onOpenWalletApp={() =>
              multiWallet.openWalletPicker(walletFamilyOf(multiWallet.activeNetwork))
            }
            walletBalances={multiWallet.walletBalances}
            balancesLoading={multiWallet.balancesLoading}
            connecting={multiWallet.connecting}
            unavailableReason={multiWallet.unavailableReason(multiWallet.activeNetwork)}
            onConnect={() => multiWallet.connect().catch(connectError)}
            onDisconnect={() => multiWallet.disconnect()}
            onRefresh={multiWallet.refreshBalances}
          />
          {linked && !connected && displayAddr && (
            <div
              style={{
                marginTop: 9,
                font: "400 12.5px/1.45 Geist, sans-serif",
                color: "#93a0b3",
              }}
            >
              {t("Connected Wallet")}
            </div>
          )}
        </div>
      </div>

      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18 }}>🤝</span>
            <span style={{ fontWeight: 600 }}>{t("Place at current rate")}</span>
          </div>
        }
        placement="bottom"
        onClose={() => setIsIssueLoanDrawerOpen(false)}
        open={isIssueLoanDrawerOpen}
        height="auto"
        bodyStyle={{
          padding: "20px 16px 36px",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div
            className="kf-tile"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "15px 16px",
              borderRadius: "14px",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onClick={() => {
              navigate("/loan-overnight", { state: { demand: false } });
              setIsIssueLoanDrawerOpen(false);
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                background: "#f0fdfa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <RocketOutlined style={{ fontSize: 20, color: "#0aa8a5" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: "14.5px", color: "#0f172a" }}>
                {t("Overnight shelf")}
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: 2 }}>
                {overnightRateNum ? `${t("Rate")} ${overnightRateNum}% · ` : ""}
                {t("Overnight returns next morning")}
              </div>
            </div>
            <RightOutlined style={{ color: "#94a3b8", fontSize: 13 }} />
          </div>

          <div
            className="kf-tile"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "15px 16px",
              borderRadius: "14px",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onClick={() => {
              navigate("/loan-overnight", { state: { demand: true } });
              setIsIssueLoanDrawerOpen(false);
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                background: "#eff6ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SyncOutlined style={{ fontSize: 20, color: "#2563eb" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: "14.5px", color: "#0f172a" }}>
                {t("On-demand shelf")}
              </div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: 2 }}>
                {demandRateNum ? `${t("Rate")} ${demandRateNum}% · ` : ""}
                {t("On-demand stop returns within 24h")}
              </div>
            </div>
            <RightOutlined style={{ color: "#94a3b8", fontSize: 13 }} />
          </div>
        </div>
      </Drawer>

      <MultiNetworkDepositDrawer
        open={isMultiDepositOpen}
        onClose={() => {
          setIsMultiDepositOpen(false);
          onVoucherDrawerClose?.();
        }}
        activeNetwork={multiWallet.activeNetwork}
        onDepositSuccess={() => {
          multiWallet.refreshBalances();
          loadInternal();
          [10000, 30000, 60000].forEach((d) => setTimeout(loadInternal, d));
        }}
      />
      <WorkingBalanceWithdrawDrawer
        open={isWorkingWithdrawOpen}
        onClose={() => setIsWorkingWithdrawOpen(false)}
        activeNetwork={multiWallet.activeNetwork}
        assets={allAssets}
        onSuccess={() => loadInternal()}
      />
    </>
  );
};

export default KredafiHome;
